import React, { FC, useEffect, useState } from 'react'
import ReactPixel from '@bettercart/react-facebook-pixel'
import { Button, Spinner, Text, Heading, Stack } from '@chakra-ui/react'
import { FcGoogle } from 'react-icons/fc'
import { useGoogleLogin } from '@react-oauth/google'
import { useNavigate } from 'react-router'
import { AUTH_REDIRECT_URL, ENABLE_META_PIXEL, META_PIXEL_ID } from '../../../constants'
import AnnouncementsPage from '../../../components/AnnouncementsPage'
import { useAuthContext } from '../../../contexts/AuthContext'
import FeatureList from '../../../components/FeatureList'
import { trackMetaConversion, generateEventId } from '../../../utils/metaConversionsUtils'

const FEATURE_LIST = [
  {
    title: '🔓 Your data stays private',
    description:
      'We only access your tabs to organize them. We never track, sell, or share your data with anyone.',
  },
  {
    title: '💡 Built for ADHD minds',
    description:
      'Developed with Carnegie Mellon researchers to work the way your brain naturally does.',
  },
  {
    title: '🤝 Easy to try, easy to stop',
    description: 'You can disconnect Skipper at any time from your browser settings.',
  },
]

const ExtensionSignedUpPage: FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthContext()

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: AUTH_REDIRECT_URL,
  })

  useEffect(() => {
    if (ENABLE_META_PIXEL) {
      const eventId = generateEventId()
      // Track server-side conversion first
      trackMetaConversion({ eventName: 'ViewContent', eventId })
      // Then track client-side pixel with same event ID for deduplication
      ReactPixel.init(META_PIXEL_ID)
      ReactPixel.track('ViewContent', {}, { eventID: eventId })
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      console.log('User is authenticated on ExtensionInstalledPage')
      setIsLoading(true)
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  const googleLoginButton = (
    <Button
      mb="4"
      as="a"
      style={{
        backgroundColor: 'black',
        borderRadius: '20px',
        borderColor: '#D5D5D5',
        color: 'white',
        fontWeight: 'normal',
        padding: '0 30px',
      }}
      onClick={googleLogin}
      leftIcon={<FcGoogle />}
    >
      Continue with Google
    </Button>
  )

  if (isLoading) {
    return <Spinner size="xl" />
  }

  return (
    <AnnouncementsPage
      heading={`One quick step to get started`}
      headingMarginBottom="24px"
      headingMaxWidth={'900px'}
      contentMaxWidth={'700px'}
      content={[
        "You're almost there! Just connect your Google account so Skipper can start organizing your tabs.",
        [
          <>
            <Stack gap={2}>
              <Heading fontSize="1.125rem">Why connect with Google?</Heading>
              <Text color="#585858">
                Just like a productivity coach needs to see your workspace to help organize it,
                Skipper needs access to your tabs to help manage them. We use this access only for
                tab management - never for tracking, selling data, or anything else.
              </Text>
            </Stack>
          </>,
        ],
      ]}
      contentMarginBottom="3rem"
      widget={googleLoginButton}
    >
      <FeatureList features={FEATURE_LIST} />
      <Text mt="2rem" pb={4} fontStyle="italic" color="#585858">
        Trusted by thousands of entrepreneurs and professionals to manage their digital workspaces
      </Text>
    </AnnouncementsPage>
  )
}

export default ExtensionSignedUpPage
